import { useMutation, useQuery } from '@apollo/client/react';
import {
  ALLOCATE_AMAZON_COUPON,
  AMAZON_COUPONS,
  AMAZON_COUPON_CODE_GROUPS,
  AMAZON_COUPON_POOLS,
  AMAZON_COUPON_PROMOTIONS,
  REGISTER_AMAZON_COUPON_PROMOTION,
  IMPORT_AMAZON_COUPON_CODES,
  RECONCILE_AMAZON_COUPONS,
} from '@staff-app/graphql-client';
import type {
  AllocateAmazonCouponInput,
  AmazonCoupon,
  AmazonCouponCodeGroup,
  AmazonCouponPage,
  AmazonCouponPoolStatus,
  AmazonCouponPromotion,
  AmazonCouponStatus,
  AmazonMarketplace,
  RegisterAmazonCouponPromotionInput,
  ImportAmazonCouponCodesInput,
} from '@staff-app/shared-types';
import { useDebounced } from './useOrderDeviceTasks';

/**
 * Rows per page, and the choices — copied from RMA_PAGE_SIZE_OPTIONS in the same folder rather
 * than re-decided (requester, 2026-09-24). Staff tables that page differently teach nothing except
 * that they were built on different days.
 *
 * The default drops from 200 to 50 with it. 200 was a single page holding the entire history,
 * which made paging theoretical and the count line decorative, and this ledger carries wide
 * columns — device, customer, reason in full, two dates — that read badly that long.
 */
export const AMAZON_PAGE_SIZE = 50;
export const AMAZON_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

/** Below this, the page warns. A pool can run dry; a mint never could. */
export const LOW_POOL_THRESHOLD = 10;

/**
 * Format a code's face value. ALWAYS takes the currency — never defaults one.
 *
 * The amount alone is not an offer: £10 off in the UK store and $10 off in the US are different
 * discounts that happen to share a number, and a screen that prints "10 off" is a screen a staffer
 * can quote wrongly to a customer.
 */
export const CURRENCY_SYMBOL: Record<string, string> = { usd: '$', gbp: '£', eur: '€' };

export function formatDiscount(amount: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency?.toLowerCase()] ?? '';
  const n = Number.isFinite(amount) ? amount : 0;
  const body = `${n.toFixed(2).replace(/\.00$/, '')} off`;
  return symbol ? `${symbol}${body}` : `${n} ${String(currency ?? '').toUpperCase()} off`;
}

/** The marketplaces migration 056's CHECK allows, in the order the picker shows them. */
export const MARKETPLACES: { value: AmazonMarketplace; label: string }[] = [
  { value: 'us', label: 'Amazon US' },
  { value: 'uk', label: 'Amazon UK' },
  { value: 'de', label: 'Amazon DE' },
];

export function useAmazonCoupons(
  search: string,
  status: string,
  marketplace: string,
  offset = 0,
  limit = AMAZON_PAGE_SIZE,
) {
  const debouncedSearch = useDebounced(search.trim(), 300);
  return useQuery<{ amazonCoupons: AmazonCouponPage }>(AMAZON_COUPONS, {
    variables: {
      filter: {
        search: debouncedSearch || undefined,
        status: (status || undefined) as AmazonCouponStatus | undefined,
        marketplace: (marketplace || undefined) as AmazonMarketplace | undefined,
      },
      pagination: { offset, limit },
    },
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * What is left to hand out, per marketplace.
 *
 * The card reads this BEFORE it will let anyone submit. The demo tab needs no equivalent because
 * minting cannot run out; finding out the pool is empty after promising a customer a code is the
 * failure this exists to prevent.
 */
export function useAmazonCouponPools() {
  return useQuery<{ amazonCouponPools: AmazonCouponPoolStatus[] }>(AMAZON_COUPON_POOLS, {
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * ROOT-only. Skipped for everyone else so a fulfilment user never fires a query they'd be refused.
 *
 * The import dialog opens on this: a top-up ATTACHES a code group to a promotion that is already
 * live. Creating another promotion costs 4-5 hours of activation for nothing.
 */
export function useAmazonCouponPromotions(enabled: boolean) {
  return useQuery<{ amazonCouponPromotions: AmazonCouponPromotion[] }>(AMAZON_COUPON_PROMOTIONS, {
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  });
}

/** ROOT-only provenance list: which CSV, who loaded it, when. */
export function useAmazonCouponCodeGroups(enabled: boolean) {
  return useQuery<{ amazonCouponCodeGroups: AmazonCouponCodeGroup[] }>(AMAZON_COUPON_CODE_GROUPS, {
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  });
}

export function useAllocateAmazonCoupon() {
  return useMutation<
    { allocateAmazonCoupon: AmazonCoupon },
    { input: AllocateAmazonCouponInput }
  >(ALLOCATE_AMAZON_COUPON, {
    // The new row belongs at the top of the ledger, AND the pool banner is now one lower — both
    // are wrong on screen the instant this succeeds, so both refetch.
    refetchQueries: ['amazonCoupons', 'amazonCouponPools'],
  });
}

/** The RARE path — 4-5 hours of Amazon-side activation follows this. */
export function useRegisterAmazonCouponPromotion() {
  return useMutation<
    { registerAmazonCouponPromotion: AmazonCouponPromotion },
    { input: RegisterAmazonCouponPromotionInput }
  >(REGISTER_AMAZON_COUPON_PROMOTION, {
    refetchQueries: ['amazonCouponPools', 'amazonCouponPromotions'],
  });
}

/** The ROUTINE path — attaches codes to a live promotion, ~1-2 minutes Amazon-side. */
export function useImportAmazonCouponCodes() {
  return useMutation<
    { importAmazonCouponCodes: AmazonCouponCodeGroup },
    { input: ImportAmazonCouponCodesInput }
  >(IMPORT_AMAZON_COUPON_CODES, {
    refetchQueries: ['amazonCouponPools', 'amazonCouponPromotions', 'amazonCouponCodeGroups'],
  });
}

/**
 * Refresh Amazon's redemption figures now instead of waiting for the daily pass. ROOT-only.
 *
 * Replaces the per-code "mark used" mutation, which asked CS to assert something they had no way
 * to know. Amazon reports redemptions per PROMOTION and never per code.
 */
export function useReconcileAmazonCoupons() {
  return useMutation<{ reconcileAmazonCoupons: number }, Record<string, never>>(
    RECONCILE_AMAZON_COUPONS,
    { refetchQueries: ['amazonCoupons', 'amazonCouponPools', 'amazonCouponPromotions'] },
  );
}

/**
 * Read a picked file as text.
 *
 * Prefers `File.text()`, which every browser we support has. The FileReader fallback is not
 * defensive padding: jsdom does not implement `File.text()`, so without it the CSV path — the one
 * that decides what lands in the pool — is the one path no test can execute. It is also what
 * runs in any environment that predates the newer API.
 */
export function readFileText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Could not read that file.'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsText(file);
  });
}

/**
 * Parse a Seller Central claim-code CSV in the BROWSER.
 *
 * The API never sees a file (see ImportAmazonCouponBatchInput) — it takes an array of codes — so
 * the parsing lives here. Seller Central's export is one code per line, usually with a header;
 * some exports carry extra columns, so the FIRST column is taken and the rest ignored.
 *
 * Returns the codes plus anything that looked wrong, so the dialog can refuse a file rather than
 * importing half of it and leaving nobody able to say what is in the pool.
 */
export function parseCodesCsv(text: string): { codes: string[]; error?: string } {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return { codes: [], error: 'That file is empty.' };

  const rows = lines.map((l) => (l.split(',')[0] ?? '').trim().replace(/^"|"$/g, ''));

  // Drop a header if the first cell is obviously a column name rather than a code. Checked by
  // WORDING, not by position: a headerless export's first line is a real code, and silently
  // discarding it would quietly shrink every batch by one.
  const first = (rows[0] ?? '').toLowerCase();
  const looksLikeHeader = /code|claim|promo|coupon/.test(first) && !/^[a-z0-9]{6,}$/i.test(rows[0] ?? '');
  const codes = (looksLikeHeader ? rows.slice(1) : rows).filter(Boolean);

  if (codes.length === 0) return { codes: [], error: 'No codes found in that file.' };

  // Caught HERE as well as server-side, because the server's message names one duplicate while
  // this can say how many — and because a paste-twice mistake should not need a round trip.
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const c of codes) {
    if (seen.has(c)) dupes.add(c);
    seen.add(c);
  }
  if (dupes.size) {
    const sample = [...dupes].slice(0, 3).join(', ');
    return {
      codes: [],
      error: `${dupes.size} duplicate code${dupes.size > 1 ? 's' : ''} in that file (${sample}${
        dupes.size > 3 ? '…' : ''
      }). Nothing was imported.`,
    };
  }

  return { codes };
}
